// Vuetify 4, configured to look like the app that already exists rather than
// like Vuetify. Everything here is derived from src/assets/global.css — the
// gradients, the accent, the five button colours — so a migrated screen sits
// next to a not-yet-migrated one without a visible seam.
//
// Icons are the SVG mdi set (@mdi/js), not the @mdi/font webfont: the webfont
// is ~1MB that every phone would download on first load, and this app is a
// PWA used in the field. The SVG set tree-shakes to only the icons in use.

import { createVuetify } from 'vuetify'
import { VApp } from 'vuetify/components/VApp'
import { aliases, mdi } from 'vuetify/iconsets/mdi-svg'
import 'vuetify/styles'

// ── Palette ────────────────────────────────────────────────────────────────
// Taken from global.css. Where a control there is a gradient, the colour here
// is its mid-point, and the gradient is re-applied by class where it matters.
const NYS = {
  // The app shell gradient runs #27253f -> #605a6d. `background` is the dark
  // end so full-bleed Vuetify surfaces match the top-left of the page.
  background: '#27253f',
  // One step lifted, so cards and menus read as above the page, not on it.
  surface: '#2e2b48',
  surfaceBright: '#3a3557',

  primary: '#6a5cff',   // .input-group input:focus border, .button-primary
  secondary: '#605a6d', // light end of the shell gradient
  info: '#45a9fc',      // .button-info
  success: '#4caf50',   // .button-success
  warning: '#ff9800',   // .button-warning
  // .button-danger is #b8576d -> #94220e. Neither end is readable as a text
  // colour on this background, so this is the lighter hover tone.
  error: '#c9574a',
}

export default createVuetify({
  // VApp is registered explicitly; every other component comes from
  // webpack-plugin-vuetify's auto-import. The shell uses <v-app> on EVERY
  // route, so if the plugin is not active — a dev server still running from
  // before vue.config.js changed, for instance — the app renders a blank
  // "Failed to resolve component: v-app" shell on every page, including
  // login. Registering this one by hand means the shell can never be the
  // thing that breaks.
  components: { VApp },

  theme: {
    defaultTheme: 'nysDark',
    themes: {
      nysDark: {
        dark: true,
        colors: {
          background: NYS.background,
          surface: NYS.surface,
          'surface-bright': NYS.surfaceBright,
          'surface-variant': NYS.secondary,
          'on-surface-variant': '#ffffff',
          primary: NYS.primary,
          secondary: NYS.secondary,
          info: NYS.info,
          success: NYS.success,
          warning: NYS.warning,
          error: NYS.error,
        },
      },
    },
  },

  icons: {
    defaultSet: 'mdi',
    aliases,
    sets: { mdi },
  },

  // App-wide component defaults. This is the piece that stops the migration
  // turning into 50 files of repeated props: set the house style once here
  // and a plain <v-text-field> already looks right.
  defaults: {
    global: {
      // The existing UI has no ripple; adding one only to migrated screens
      // would make the two halves feel different.
      ripple: false,
    },
    VTextField: {
      variant: 'outlined',
      density: 'comfortable',
      hideDetails: 'auto',
    },
    VSelect: {
      variant: 'outlined',
      density: 'comfortable',
      hideDetails: 'auto',
    },
    VTextarea: {
      variant: 'outlined',
      density: 'comfortable',
      hideDetails: 'auto',
    },
    VCheckbox: {
      density: 'comfortable',
      hideDetails: 'auto',
      color: 'primary',
    },
    VCard: {
      color: 'surface',
      rounded: 'lg',
    },
    VDataTable: {
      density: 'comfortable',
      hover: true,
    },
    VBtn: {
      // global.css buttons are not uppercase; Vuetify's default is.
      class: 'text-none',
      variant: 'flat',
    },
  },
})
